// Drifting just-intonation pitch field: root drift + reroll, interval widening,
// snap(), wild gate, harmonic gate (<30 cent collision rejection). Pure.

import type { PitchFieldSnapshot } from '@shared/parameterState';
import type { Rng } from '@shared/rng';
import { nextRange } from '@shared/rng';

export const BASE_RATIOS: readonly number[] = [1, 9 / 8, 6 / 5, 4 / 3, 3 / 2, 8 / 5, 9 / 5, 2];

/** Named scales (octave-relative ratio sets) a preset can select. */
export const SCALES: Readonly<Record<string, readonly number[]>> = Object.freeze({
  just: BASE_RATIOS,
  pentatonic: [1, 9 / 8, 5 / 4, 3 / 2, 5 / 3],
  wholeTone: [1, 1.12246, 1.25992, 1.41421, 1.5874, 1.78180],
  fifths: [1, 3 / 2], // open, droney
  dorian: [1, 9 / 8, 6 / 5, 4 / 3, 3 / 2, 5 / 3, 16 / 9],
  chromatic: Array.from({ length: 12 }, (_, i) => Math.pow(2, i / 12)),
  microtonal: Array.from({ length: 24 }, (_, i) => Math.pow(2, i / 24)),
});

export const HARMONIC_GATE_CENTS = 30;
const OCTAVE_K_MIN = -2;
const OCTAVE_K_MAX = 3;
const RING_SIZE = 16;
/** How far intervals stretch per unit of `widening`. */
const WIDEN_FACTOR_PER_UNIT = 0.4;

export function cents(a: number, b: number): number {
  return 1200 * Math.log2(a / b);
}

/** Stretch a ratio set: intervals widen as `widening` rises; ratio 1 stays at 1. */
export function widenRatios(ratios: readonly number[], widening: number): number[] {
  const factor = 1 + widening * WIDEN_FACTOR_PER_UNIT;
  return ratios.map((r) => 1 + (r - 1) * factor);
}

/** Widened ratio set over the default just-intonation base. */
export function widenedRatios(widening: number): number[] {
  return widenRatios(BASE_RATIOS, widening);
}

interface ActiveVoice {
  hz: number;
  expiresEngineSec: number;
}

export class PitchField {
  rootHz: number;
  private rootTargetHz: number;
  widening = 0;
  private rerollAtSec: number;
  private active: ActiveVoice[] = [];
  private scale: readonly number[] = BASE_RATIOS;

  constructor(rng: Rng, initialRootHz = 165) {
    this.rootHz = initialRootHz;
    this.rootTargetHz = initialRootHz;
    this.rerollAtSec = nextRange(rng, 60, 120);
  }

  /** Select the active scale (preset-driven). Falls back to `just` if unknown/empty. */
  setScale(ratios: readonly number[] | undefined): void {
    this.scale = ratios && ratios.length ? ratios : BASE_RATIOS;
  }

  /** This field's widened scale (instance scale stretched by `widening`). */
  private widened(): number[] {
    return widenRatios(this.scale, this.widening);
  }

  /** Advance drift; reroll root target every 60-120s. registerHz anchors the new root. */
  step(engineTimeSec: number, dt: number, registerHz: number, harmonicityNorm: number, rng: Rng): void {
    if (engineTimeSec >= this.rerollAtSec) {
      // root near current register within ~110..220 Hz, jittered +/- a quartertone-ish
      const cand = registerHz * Math.pow(2, nextRange(rng, -0.25, 0.25));
      this.rootTargetHz = clampHz(cand, 110, 220);
      this.rerollAtSec = engineTimeSec + nextRange(rng, 60, 120);
    }
    // one-pole the root in Hz space using exact-exp alpha
    const aRoot = 1 - Math.exp(-dt / 40);
    this.rootHz += aRoot * (this.rootTargetHz - this.rootHz);
    // widening toward 0.15 + 0.5*harmonicityNorm
    const widenTarget = 0.15 + 0.5 * harmonicityNorm;
    const aW = 1 - Math.exp(-dt / 50);
    this.widening += aW * (widenTarget - this.widening);

    // expire stale active voices
    if (this.active.length) {
      this.active = this.active.filter((v) => v.expiresEngineSec > engineTimeSec);
    }
  }

  snapshot(): PitchFieldSnapshot {
    return { rootHz: this.rootHz, ratios: this.widened(), widening: this.widening };
  }

  /** Snap a freq to the nearest scale pitch over octaves [-2,3]. */
  snap(freqHz: number): { hz: number; centsOff: number } {
    const ratios = this.widened();
    let best = freqHz;
    let bestCents = Infinity;
    for (let k = OCTAVE_K_MIN; k <= OCTAVE_K_MAX; k++) {
      const oct = Math.pow(2, k);
      for (const r of ratios) {
        const cand = this.rootHz * r * oct;
        const d = Math.abs(cents(freqHz, cand));
        if (d < bestCents) {
          bestCents = d;
          best = cand;
        }
      }
    }
    return { hz: best, centsOff: bestCents };
  }

  /**
   * Resolve a desired pitch into a final pitch + snapped flag.
   * wild -> raw pitch, bypasses snap + harmonic gate.
   * snapped -> snap, then harmonic gate: if within 30 cents of an active snapped voice,
   * nudge to next scale degree once, else reject (return null).
   */
  resolve(
    desiredHz: number,
    wild: boolean,
    engineTimeSec: number,
    durationHintSec: number,
  ): { hz: number; snapped: boolean } | null {
    if (wild) {
      return { hz: desiredHz, snapped: false };
    }
    const snapped = this.snap(desiredHz);
    let hz = snapped.hz;
    if (this.collides(hz)) {
      const nudged = this.nextDegree(hz);
      if (nudged === null || this.collides(nudged)) {
        return null; // reject — better silence than mud
      }
      hz = nudged;
    }
    this.pushActive(hz, engineTimeSec + durationHintSec);
    return { hz, snapped: true };
  }

  private collides(hz: number): boolean {
    for (const v of this.active) {
      // fold to nearest octave: distance to unison or octave
      let d = cents(hz, v.hz);
      d = d - 1200 * Math.round(d / 1200);
      if (Math.abs(d) < HARMONIC_GATE_CENTS) return true;
    }
    return false;
  }

  /** Next scale degree up (else down) from a snapped pitch. */
  private nextDegree(hz: number): number | null {
    const ratios = this.widened();
    // build sorted candidate list across octaves
    const cands: number[] = [];
    for (let k = OCTAVE_K_MIN; k <= OCTAVE_K_MAX; k++) {
      for (const r of ratios) cands.push(this.rootHz * r * Math.pow(2, k));
    }
    cands.sort((a, b) => a - b);
    // find index of current hz (closest)
    let idx = 0;
    let bestD = Infinity;
    for (let i = 0; i < cands.length; i++) {
      const d = Math.abs(cents(cands[i], hz));
      if (d < bestD) {
        bestD = d;
        idx = i;
      }
    }
    if (idx + 1 < cands.length) return cands[idx + 1];
    if (idx - 1 >= 0) return cands[idx - 1];
    return null;
  }

  private pushActive(hz: number, expiresEngineSec: number): void {
    this.active.push({ hz, expiresEngineSec });
    if (this.active.length > RING_SIZE) this.active.shift();
  }

  /** Test/inspection helper. */
  activeCount(): number {
    return this.active.length;
  }
}

function clampHz(hz: number, lo: number, hi: number): number {
  if (hz < lo) return lo;
  if (hz > hi) return hi;
  return hz;
}

/** Effective wild probability for a mood baseline + pureRough dial. */
export function wildProbability(baseWild: number, pureRough: number): number {
  const p = baseWild + 0.25 * Math.max(0, pureRough);
  return p < 0 ? 0 : p > 1 ? 1 : p;
}
